import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSearchParams } from "react-router-dom";
import {
  SkillSheetMainStepperProvider,
  useSkillSheetMainStepper,
} from "../../context/SkillSheetMainStepperContext";
import ProjectInfoForm from "../../form/ProjectInfoForm";
import CategoryContentForm from "../../form/CategoryContentForm";
import LanguageFrameworkForm from "../../form/LanguageFrameworkForm";
import BusinessSystemForm from "../../form/BusinessSystemForm";
import CloudServiceInfoForm from "../../form/CloudServiceInfoForm";
import ServerOperatingSystemForm from "../../form/ServerOperatingSystemForm";
import MultiSelectForm from "../../form/MultiSelectForm";
import MiddlewareInfoForm from "../../form/MiddlewareInfoForm";
import { toast } from "react-toastify";
import UserSuggestionsWidget from "../../../user/suggestions/UserSuggestionsWidget";
import StepperNavigation from "../../shared/StepperNavigation";
import DemoInputInfoNote from "../../shared/DemoInputInfoNote";
import DemoRegisterModal from "../../shared/DemoRegisterModal";
import DemoFormActions from "../../shared/DemoFormActions";
import DemoStorageAlert from "../../shared/DemoStorageAlert";
import DemoPdfConfirmButton from "../../shared/DemoPdfConfirmButton";
import { goToDemoSignup, isDemoStepLocked } from "../../utils/demoConstants";
import { trackDemoEvent } from "../../utils/demoTracking";

// ロックされたステップの説明（aria-describedby で参照）
const LOCKED_STEP_DESCRIPTION_ID = "skill-sheet-main-locked-step-description";

const steps = [
  "プロジェクト情報",
  "要件定義",
  "基本設計",
  "詳細設計",
  "製造/構築",
  "テスト",
  "運用/保守",
  "フロントエンド",
  "バックエンド",
  "業務システム",
  "クラウド",
  "Server OS",
  "クライアントOS",
  "MW",
  "DB",
  "Network",
  "デザイン",
  "ツール",
];

// メインコンポーネント（ProviderでラップされたContext内部）
function SkillSheetMainCreateStepperContent() {
  const [activeStep, setActiveStep] = useState(0);
  const stepperStickyRef = useRef(null);
  const stepperContainerRef = useRef(null);
  const prevStepRef = useRef(0);
  const {
    runStepValidation,
    submitForm,
    demo,
    flushDemoDraft,
    watch,
    getValues,
  } = useSkillSheetMainStepper();
  const [saving, setSaving] = useState(false);
  // デモの本登録誘導モーダル（reason: "save" | "locked" | "transfer"）。
  // saveFailed は開く直前の保存結果。モーダルの文面を引き継げない旨に切り替えるのに使う
  const [demoModal, setDemoModal] = useState({
    open: false,
    reason: "save",
    saveFailed: false,
  });
  // デモ：入力をリセットしたことを通知（Step が変わらなくても上部へ戻すため）
  const [demoResetTick, setDemoResetTick] = useState(0);

  // デモモードでロックされているステップか
  const isStepLocked = (index) => isDemoStepLocked(demo, index);

  // デモ：ステッパー上部（.demo_browser の先頭）へスクロール
  const scrollDemoToStepperTop = useCallback(() => {
    const target =
      stepperStickyRef.current?.closest(".demo_browser") ||
      stepperStickyRef.current;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const closeDemoModal = () => {
    trackDemoEvent("demo_register_modal_close");
    setDemoModal((prev) => ({ ...prev, open: false }));
  };

  // デモ：下書きを即時保存してから本登録誘導モーダルを開く。
  // 保存の成否によらず案内は見せる（失敗時はモーダル側が引き継げない旨に切り替わる）
  const openDemoModal = (reason) => {
    const saved = flushDemoDraft();
    setDemoModal({ open: true, reason, saveFailed: !saved });
  };

  // デモ：フォーム下の「本登録して内容を引き継ぐ」。
  // 保存できていればそのまま本登録へ。できなければ案内モーダルで引き継げない旨を伝えて選ばせる
  const handleSignupTransfer = () => {
    if (flushDemoDraft()) {
      goToDemoSignup("cta");
      return;
    }

    setDemoModal({ open: true, reason: "transfer", saveFailed: true });
  };

  // コンポーネントマウント時にスクロール位置を一番左に設定
  useEffect(() => {
    if (stepperContainerRef.current) {
      stepperContainerRef.current.scrollTo({
        left: 0,
        behavior: "auto",
      });
    }
  }, []);

  // ステップ移動したときにステップバーのスクロール位置を調整する
  useEffect(() => {
    if (stepperContainerRef.current) {
      const stepperElement = stepperContainerRef.current;
      const containerWidth = stepperElement.offsetWidth;
      const stepWidth = 120;
      const activeStepPosition = activeStep * stepWidth;
      const scrollPosition =
        activeStepPosition - containerWidth / 2 + stepWidth / 2;

      stepperElement.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: "smooth",
      });
    }
  }, [activeStep]);

  // ステップ移動したときにページの最上部までスクロール。
  // デモ（TOPページ埋め込み）ではページ全体を最上部に飛ばさず、
  // 固定ヘッダーのすぐ下にステッパーが来る位置までスクロールして戻す。
  useEffect(() => {
    if (demo) {
      // 実際にステップが変わったときだけスクロールする
      // （初回表示や StrictMode の二重実行では発火させない）
      const stepChanged = prevStepRef.current !== activeStep;
      prevStepRef.current = activeStep;
      if (!stepChanged) return;

      // .demo_browser は top/index.html.erb 側。ヘッダー分は CSS の scroll-margin-top で吸収
      scrollDemoToStepperTop();
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [activeStep, demo, scrollDemoToStepperTop]);

  // デモ：入力をリセットしたら Step が変わらなくても上部へ戻す
  useEffect(() => {
    if (demo && demoResetTick > 0) scrollDemoToStepperTop();
  }, [demoResetTick, demo, scrollDemoToStepperTop]);

  // デモ：最初に入力した項目名を1回だけ計測する（入力値は送らない）
  useEffect(() => {
    if (!demo) return;
    const subscription = watch((_values, { name }) => {
      // 復元・リセットや、フォームが自動で空にする処理は入力開始にしない
      const value = name ? getValues(name) : null;
      if (value == null || value === "") return;
      trackDemoEvent("demo_input_start", { demo_first_field: name });
      subscription.unsubscribe();
    });
    return () => subscription.unsubscribe();
  }, [demo, watch, getValues]);

  // ステップ移動のバリデーション
  const handleStepChange = (newStep) => {
    // デモでロックされた step は移動せず、本登録誘導モーダルを出す
    if (isStepLocked(newStep)) {
      openDemoModal("locked");
      return;
    }

    const isValid = runStepValidation(activeStep);

    if (isValid) {
      // デモ：Step 移動時は下書きを即時保存する
      if (demo) flushDemoDraft();
      setActiveStep(newStep);
    }
    // バリデーションエラーがある場合は移動しない
  };

  const handleNext = () => {
    handleStepChange(activeStep + 1);
  };

  const handleBack = () => {
    // 前へボタンでもバリデーションを実行
    handleStepChange(activeStep - 1);
  };

  const handleStepClick = (step) => {
    handleStepChange(step);
  };

  // 現在のステップをバリデーションして、問題なければ保存する
  const handleSave = async () => {
    // デモでは submitForm せず、下書きを即時保存して本登録誘導モーダルを出す
    if (demo) {
      openDemoModal("save");
      return;
    }

    const isValid = runStepValidation(activeStep);
    if (!isValid) return;

    const isLastStep = activeStep === steps.length - 1;

    setSaving(true);

    try {
      await submitForm({
        navigateOnSuccess: isLastStep,
        successMessage: isLastStep
          ? "プロジェクトを保存しました。"
          : "一時保存しました。",
      });
    } finally {
      setSaving(false);
    }
  };

  // ステップごとの入力フォームを表示
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <ProjectInfoForm />;
      case 1:
        return (
          <CategoryContentForm
            title="要件定義"
            fieldName="requirements_definition_operations"
            masterDataKey="requirements_definition_operations"
            stepIndex={1}
          />
        );
      case 2:
        return (
          <CategoryContentForm
            title="基本設計"
            fieldName="basic_design_operations"
            masterDataKey="basic_design_operations"
            stepIndex={2}
          />
        );
      case 3:
        return (
          <CategoryContentForm
            title="詳細設計"
            fieldName="detail_design_operations"
            masterDataKey="detail_design_operations"
            stepIndex={3}
          />
        );
      case 4:
        return (
          <CategoryContentForm
            title="製造/構築"
            fieldName="program_operations"
            masterDataKey="program_operations"
            stepIndex={4}
          />
        );
      case 5:
        return (
          <CategoryContentForm
            title="テスト"
            fieldName="test_operations"
            masterDataKey="test_operations"
            stepIndex={5}
          />
        );
      case 6:
        return (
          <CategoryContentForm
            title="運用/保守"
            fieldName="maintenance_operations"
            masterDataKey="maintenance_operations"
            stepIndex={6}
          />
        );
      case 7:
        return (
          <LanguageFrameworkForm
            title="フロントエンド"
            fieldName="frontend_languages"
            masterDataKey="frontend_languages"
            frameworkType="front_end"
            stepIndex={7}
          />
        );
      case 8:
        return (
          <LanguageFrameworkForm
            title="バックエンド"
            fieldName="backend_languages"
            masterDataKey="backend_languages"
            frameworkType="back_end"
            stepIndex={8}
          />
        );
      case 9:
        return <BusinessSystemForm />;
      case 10:
        return <CloudServiceInfoForm />;
      case 11:
        return <ServerOperatingSystemForm />;
      case 12:
        return (
          <MultiSelectForm
            title="クライアントOS"
            fieldName="selectedOperatingSystems"
            masterDataKey="operating_systems"
            backendFieldName="operating_systems"
            stepIndex={12}
            placeholder="クライアントOSを選択または入力してください"
          />
        );
      case 13:
        return <MiddlewareInfoForm />;
      case 14:
        return (
          <MultiSelectForm
            title="DB"
            fieldName="selectedDbs"
            masterDataKey="dbs"
            backendFieldName="dbs"
            stepIndex={14}
            placeholder="データベースを選択または入力してください"
          />
        );
      case 15:
        return (
          <MultiSelectForm
            title="Network"
            fieldName="selectedNetworks"
            masterDataKey="networks"
            backendFieldName="networks"
            stepIndex={15}
            placeholder="ネットワークを選択または入力してください"
          />
        );
      case 16:
        return (
          <MultiSelectForm
            title="デザイン"
            fieldName="selectedDesignTools"
            masterDataKey="design_tools"
            backendFieldName="design_tools"
            stepIndex={16}
            placeholder="デザインツールを選択または入力してください"
          />
        );
      case 17:
        return (
          <MultiSelectForm
            title="ツール"
            fieldName="selectedTools"
            masterDataKey="tools"
            backendFieldName="tools"
            stepIndex={17}
            placeholder="ツールを選択または入力してください"
          />
        );
      default:
        return "不明なステップ";
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ width: "100%" }}>
        {/* 固定部分 */}
        <Box
          ref={stepperStickyRef}
          sx={{
            position: "sticky",
            top: { xs: "45px", sm: "63px" },
            zIndex: 20,
            backgroundColor: "#F4F7FA",
            boxShadow: 0.5,
            mx: -2,
            px: { xs: 2, sm: 3 },
            pt: { xs: 1, sm: 1.5 },
            pb: 1,
            mb: demo ? 0 : 2,
          }}
        >
          {/* ステップ表示部分 */}
          <Box
            ref={stepperContainerRef}
            sx={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              mb: demo ? 0 : 4,
              pb: 1,
              // スクロールバーのスタイリング
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f5f5f5",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d4d4d8",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#a1a1aa",
                },
              },
            }}
          >
            <Stepper
              activeStep={activeStep}
              nonLinear={true}
              orientation="horizontal"
              sx={{
                // ステップ全体のコンテナ
                minWidth: `${steps.length * 120}px`, // connectorのスペースを考慮して少し広げる
                width: `${steps.length * 120}px`,
                "& .MuiStepper-root": {
                  padding: 0,
                },
                // コネクターライン（ステップ間の線）
                "& .MuiStepConnector-root": {
                  flex: "1 1 auto",
                  minWidth: "20px", // connectorの最小幅を明示的に指定
                  "& .MuiStepConnector-line": {
                    borderTopWidth: "2px",
                    borderTopStyle: "solid",
                    minHeight: "2px",
                    width: "100%", // 線の幅を100%に設定
                  },
                },
                // アクティブ/完了したステップのコネクター
                "& .MuiStepConnector-active .MuiStepConnector-line, & .MuiStepConnector-completed .MuiStepConnector-line":
                  {
                    borderColor: "#4F46E5",
                  },
                // ステップコンテナ
                "& .MuiStep-root": {
                  flex: "1 1 auto", // connectorが表示されるように元に戻す
                  minWidth: "100px", // 最小幅を少し減らす
                  padding: "0 2px",
                },
                // ステップアイコン
                "& .MuiStepIcon-root": {
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  "&.Mui-active": {
                    color: "#4F46E5",
                    backgroundColor: "#4F46E5",
                    "& .MuiStepIcon-text": {
                      fill: "white",
                    },
                  },
                  "&.Mui-completed": {
                    color: "#4F46E5",
                    backgroundColor: "#4F46E5",
                    "& .MuiStepIcon-text": {
                      fill: "white",
                    },
                  },
                  "& .MuiStepIcon-text": {
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    fill: "white",
                  },
                },
                // ステップボタン（キーボード操作のフォーカス表示）
                "& .MuiStepButton-root": {
                  padding: "4px",
                  margin: 0,
                  borderRadius: "4px",
                  "&.Mui-focusVisible": {
                    outline: "2px solid #4F46E5",
                    outlineOffset: "-2px",
                  },
                },
                // ステップラベル
                "& .MuiStepLabel-root": {
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  "&:hover": {
                    opacity: 0.8,
                  },
                },
                "& .MuiStepLabel-iconContainer": {
                  paddingRight: 0,
                  marginBottom: "8px",
                },
                "& .MuiStepLabel-labelContainer": {
                  width: "100%",
                  textAlign: "center",
                },
                "& .MuiStepLabel-label": {
                  fontSize: "0.75rem",
                  lineHeight: "1.2",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100px",
                  margin: "0 auto",
                  "&.Mui-active": {
                    color: "#4F46E5",
                    fontWeight: "bold",
                  },
                  "&.Mui-completed": {
                    color: "#4F46E5",
                  },
                },
              }}
            >
              {steps.map((label, index) => {
                const locked = isStepLocked(index);
                // ロック中も押下で本登録モーダルを出すため disabled にはせず、見た目だけグレーにする
                return (
                  <Step key={label} completed={false}>
                    <StepButton
                      onClick={() => handleStepClick(index)}
                      aria-describedby={
                        locked ? LOCKED_STEP_DESCRIPTION_ID : undefined
                      }
                      sx={
                        locked
                          ? { "& .MuiStepLabel-label": { color: "#68707e" } }
                          : undefined
                      }
                      icon={
                        locked ? (
                          // 番号はそのまま出しつつ、右下に鍵アイコンをバッジ風に添える
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-flex",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#e5e7eb",
                                color: "#9ca3af",
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                              }}
                            >
                              {index + 1}
                            </Box>
                            <LockOutlinedIcon
                              sx={{
                                position: "absolute",
                                right: -5,
                                bottom: -3,
                                fontSize: 13,
                                color: "#9ca3af",
                              }}
                            />
                          </Box>
                        ) : undefined
                      }
                    >
                      {label}
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>
            {demo && (
              <Box
                component="span"
                id={LOCKED_STEP_DESCRIPTION_ID}
                sx={{ display: "none" }}
              >
                本登録が必要なステップ
              </Box>
            )}
          </Box>
        </Box>

        {/* デモ：PDFで確認する */}
        {demo && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5 }}>
            <DemoPdfConfirmButton />
          </Box>
        )}

        {/* デモ：localStorage エラーの通知 */}
        {demo && <DemoStorageAlert />}

        {/* フォーム表示部分 */}
        {activeStep !== steps.length && (
          <Box
            sx={{
              bgcolor: "white",
              p: 3,
              borderRadius: 2,
              boxShadow:
                "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* ステップごとの入力フォームを表示 */}
            {getStepContent(activeStep)}

            {/* デモの入力情報の取扱い（step1 のみ・ボタンの直前） */}
            {demo && activeStep === 0 && <DemoInputInfoNote />}

            <StepperNavigation
              activeStep={activeStep}
              stepsLength={steps.length}
              onBack={handleBack}
              onNext={handleNext}
              onSave={handleSave}
              saving={saving}
            />
          </Box>
        )}

        {/* デモ：フォームの外（下）に置くアクション（リセット / 本登録して引き継ぐ） */}
        {demo && (
          <DemoFormActions
            onReset={() => {
              setActiveStep(0);
              setDemoResetTick((n) => n + 1);
            }}
            onSignupTransfer={handleSignupTransfer}
          />
        )}
      </Box>

      {/* サジェストウィジェット（デモモードでは要ログインAPIを叩くため非表示） */}
      {!demo && (
        <UserSuggestionsWidget step={activeStep} anchorRef={stepperStickyRef} />
      )}

      {/* デモ：一時保存 / ロックされた step 押下時の本登録誘導モーダル。
          確定時は保存できていなくても localStorage に残っている下書きをそのまま引き継ぐ */}
      {demo && (
        <DemoRegisterModal
          open={demoModal.open}
          reason={demoModal.reason}
          saveFailed={demoModal.saveFailed}
          onClose={closeDemoModal}
          onConfirm={() => goToDemoSignup(demoModal.reason)}
        />
      )}
    </Container>
  );
}

// Providerでラップした外側のコンポーネント
export default function SkillSheetMainCreateStepper({ demo = false }) {
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setError(false);

    // デモモード（TOPページ埋め込み）：要ログインの new.json を叩かず、空の値で起動する
    if (demo) {
      setFormData({
        csrfToken: "",
        formAction: "",
        targetUserId: null,
        targetUserType: "user",
      });
      return;
    }

    // ?company_user_id=8 とか ?user_id=41 を拾う
    const searchParamsString = searchParams.toString();
    const searchString = searchParamsString ? `?${searchParamsString}` : "";

    fetch(`/skill_sheet_mains/new.json${searchString}`)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to fetch skill sheet main new data: ${res.status} ${res.statusText}`,
          );
        }
        return res.json();
      })
      .then((data) => {
        setFormData({
          csrfToken: data.csrf_token,
          formAction: data.form_action,
          targetUserId: data.target_user_id,
          targetUserType: data.target_user_type,
        });
      })
      .catch((e) => {
        console.error(e);
        setError(true);
        toast.error("読み込みに失敗しました。", { toastId: "fetch-error" });
      });
  }, [searchParams, demo]);

  if (error) {
    return (
      <Box p={4} ml={4}>
        <Typography>
          読み込みに失敗しました。
          <br />
          通信環境をご確認し、再読み込みしてください。
        </Typography>
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  const { csrfToken, formAction, targetUserId, targetUserType } = formData;

  return (
    <SkillSheetMainStepperProvider
      csrfToken={csrfToken}
      formAction={formAction}
      targetUserId={targetUserId}
      targetUserType={targetUserType}
      demo={demo}
    >
      <SkillSheetMainCreateStepperContent />
    </SkillSheetMainStepperProvider>
  );
}
